import { useMemo } from "react";
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { UnfoldMore as UnfoldMoreIcon } from "@mui/icons-material";

import { TransferTask, useTransferQueue } from "./app/transferQueue";
import { humanReadableSize } from "./app/utils";

/**
 * Floating upload indicator. Purely a view over the transfer queue's existing
 * loaded/total counters — it does not participate in the upload itself.
 */
function UploadProgressBar({ onExpand }: { onExpand: () => void }) {
  const transferQueue: TransferTask[] = useTransferQueue();

  const { active, loaded, total, done, count } = useMemo(() => {
    const uploads = Object.values(transferQueue).filter(
      (task) => task.type === "upload"
    );
    const active = uploads.filter((task) =>
      ["pending", "in-progress"].includes(task.status)
    );
    return {
      active,
      count: uploads.length,
      done: uploads.filter((task) => task.status === "completed").length,
      loaded: active.reduce((sum, task) => sum + task.loaded, 0),
      total: active.reduce((sum, task) => sum + task.total, 0),
    };
  }, [transferQueue]);

  const uploading = active.length > 0;
  const percent = total > 0 ? Math.min(100, (loaded / total) * 100) : 0;
  const current = active.find((task) => task.status === "in-progress");

  return (
    <Collapse in={uploading} unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          right: 16,
          mx: "auto",
          maxWidth: 420,
          zIndex: 1200,
          px: 2,
          py: 1.25,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap title={current?.name}>
                {current
                  ? `Uploading ${current.name}`
                  : "Preparing upload…"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(percent)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant={total > 0 ? "determinate" : "indeterminate"}
              value={percent}
              sx={{ height: 6, borderRadius: 3, my: 0.75 }}
            />
            <Typography variant="caption" color="text.secondary">
              {humanReadableSize(loaded)} / {humanReadableSize(total)}
              {count > 1 && ` · ${done}/${count} files`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onExpand} aria-label="Show all transfers">
            <UnfoldMoreIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>
    </Collapse>
  );
}

export default UploadProgressBar;
